/**
 * useBranding composable
 *
 * Two entry-points:
 *  - loadBrandingFromMe()  — for authenticated flows: fetches /api/me and
 *                            applies company branding if the response includes it.
 *  - applyStoredBranding() — for the register/onboarding flow: reads the
 *                            branding stored in the register store (set during
 *                            screening / token lookup) and applies it, falling
 *                            back to the "af-branding" cookie when the stores are
 *                            empty (return from the assessments app).

 *
 * Both call applyBranding() from utils/branding.ts which sets CSS custom
 * properties on :root, mirroring what af/auth does server-side in its layout.
 */
import { useCookies } from '@/composables/useCookies';
import { useOnboardingStore } from '@/stores/onboarding';
import { useRegisterStore } from '@/stores/register';
import { useUserStore } from '@/stores/user';
import { applyBranding, type CompanyBranding } from '@/utils/branding';

const BRANDING_COOKIE = 'af-branding';

/**
 * Branding coming back from the assessments app: the screening / invite / workflow
 * controllers write the "af-branding" cookie (raw, not httpOnly) before handing the
 * candidate over, and it is still there when they come back to a page whose store is
 * empty. Mirrors getCompanyBranding() in af/auth.
 * The cookie is not httpOnly, so its content cannot be trusted — anything invalid
 * yields null instead of throwing. `{}` (written when a screening has no branding)
 * has no colour and is therefore ignored.
 */
function getBrandingFromCookie(): CompanyBranding | null {
  const branding = useCookies().getJSON<CompanyBranding>(BRANDING_COOKIE);

  if (!branding?.color?.cta && !branding?.color?.theme) return null;

  return branding;
}

export function useBranding() {
  /**
   * Fetch /api/me via the user store and apply company branding.
   * Silently fails if the user is not authenticated or if no branding is found.
   */
  async function loadBrandingFromMe(): Promise<void> {
    try {
      const userStore = useUserStore();
      const user = await userStore.fetch();

      const branding: CompanyBranding | undefined = user.company?.branding;

      if (branding) applyBranding(branding);
    } catch {
      // Not authenticated or API unavailable — CSS defaults remain active
    }
  }

  /**
   * Apply the branding already stored in the register store.
   * Call this on register / onboarding views where /api/me is not yet
   * available (user just registered and token is being set up).
   */
  function applyStoredBranding(): void {
    // The register store is reset on the way into the onboarding, which hands its
    // branding over to the onboarding store — fall back to it, otherwise a reload
    // on an onboarding step comes back with the default AssessFirst palette.
    const branding = useRegisterStore().branding ?? useOnboardingStore().branding ?? getBrandingFromCookie();
    if (branding) applyBranding(branding);
  }

  return { loadBrandingFromMe, applyStoredBranding };
}
